import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchJSON } from '../api';
import { STORAGE_KEYS } from '@/constants/storage-keys';
import { logger } from '@/lib/logger';

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [selected, setSelectedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const projectsRef = useRef([]);

  // Wrap setSelected so the localStorage write stays authoritative and
  // other tabs notice via the `storage` event.
  const setSelected = useCallback((next) => {
    setSelectedState(next);
    try {
      if (next?.id) {
        window.localStorage.setItem(STORAGE_KEYS.SELECTED_PROJECT_ID, next.id);
      } else {
        window.localStorage.removeItem(STORAGE_KEYS.SELECTED_PROJECT_ID);
      }
    } catch {
      // quota / disabled storage — ignore
    }
  }, []);

  const loadProjects = useCallback(() => {
    return fetchJSON('/')
      .then((data) => {
        setProjects(data);
        projectsRef.current = data;
        setSelectedState((prev) => {
          // 1. Keep current selection if still valid
          if (prev) {
            const updated = data.find((p) => p.id === prev.id);
            if (updated) return updated;
          }
          // 2. Restore from localStorage (set by ProjectHub or another tab)
          const savedId = localStorage.getItem(STORAGE_KEYS.SELECTED_PROJECT_ID);
          if (savedId) {
            const saved = data.find((p) => p.id === savedId);
            if (saved) return saved;
          }
          // 3. Auto-select first project as fallback
          return data.length > 0 ? data[0] : null;
        });
        return data;
      })
      .catch((err) => logger.error('Failed to load projects', err));
  }, []);

  useEffect(() => {
    loadProjects().finally(() => setLoading(false));
  }, [loadProjects]);

  // Cross-tab sync: if the user switches project in another tab, reflect here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEYS.SELECTED_PROJECT_ID) return;
      const newId = e.newValue;
      if (!newId) {
        setSelectedState(null);
        return;
      }
      const match = projectsRef.current.find((p) => p.id === newId);
      if (match) setSelectedState(match);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ProjectContext.Provider value={{ projects, selected, setSelected, loading, refreshProjects: loadProjects }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used inside ProjectProvider');
  return ctx;
}
